/**
 * Wide pagoda-roofline silhouette — used as the banner element at the top of
 * the Forecourt page (and reusable elsewhere). Three tiers of upturned-corner
 * roofs stacked, with hanging tassels and a central jewel. Drawn from a Mann's
 * Chinese Theatre side-elevation reference.
 */
export function PagodaRoofline({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 1200 220"
      preserveAspectRatio="xMidYMin slice"
      className={className}
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pagoda-gold" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#E8C977" />
          <stop offset="55%" stopColor="#C7A24A" />
          <stop offset="100%" stopColor="#8B6A22" />
        </linearGradient>
        <linearGradient id="pagoda-tile" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#3B2A1F" />
          <stop offset="100%" stopColor="#1A0B0B" />
        </linearGradient>
      </defs>

      {/* Lowest roof — widest */}
      <path
        d="M 40 200
           Q 40 170 80 168
           L 130 168
           Q 130 130 200 110
           L 1000 110
           Q 1070 130 1070 168
           L 1120 168
           Q 1160 170 1160 200 Z"
        fill="url(#pagoda-tile)"
        stroke="#C7A24A"
        strokeWidth="1.5"
      />
      {/* Lower-roof tile lines */}
      <g stroke="#C7A24A" strokeWidth="0.6" opacity="0.7">
        {Array.from({ length: 28 }).map((_, i) => {
          const x = 200 + i * 30;
          return <line key={i} x1={x} y1="113" x2={x} y2="168" />;
        })}
      </g>
      {/* Lower-roof gold trim band */}
      <rect x="200" y="155" width="800" height="6" fill="url(#pagoda-gold)" />
      {/* Lower-roof upturned eaves — tassels */}
      <g fill="url(#pagoda-gold)">
        <path d="M 125 168 Q 110 178 100 188 L 130 184 Z" />
        <path d="M 1075 168 Q 1090 178 1100 188 L 1070 184 Z" />
      </g>

      {/* Middle roof */}
      <path
        d="M 240 110
           Q 240 90 290 88
           L 320 88
           Q 320 60 380 48
           L 820 48
           Q 880 60 880 88
           L 910 88
           Q 960 90 960 110 Z"
        fill="url(#pagoda-tile)"
        stroke="#C7A24A"
        strokeWidth="1.5"
      />
      <g stroke="#C7A24A" strokeWidth="0.5" opacity="0.7">
        {Array.from({ length: 18 }).map((_, i) => {
          const x = 380 + i * 25;
          return <line key={i} x1={x} y1="51" x2={x} y2="88" />;
        })}
      </g>
      <rect x="380" y="78" width="440" height="4" fill="url(#pagoda-gold)" />
      <g fill="url(#pagoda-gold)">
        <path d="M 315 88 Q 302 96 295 106 L 320 100 Z" />
        <path d="M 885 88 Q 898 96 905 106 L 880 100 Z" />
      </g>

      {/* Top roof — smallest */}
      <path
        d="M 440 48
           Q 440 30 480 28
           L 510 28
           Q 510 12 600 8
           Q 690 12 690 28
           L 720 28
           Q 760 30 760 48 Z"
        fill="url(#pagoda-tile)"
        stroke="#C7A24A"
        strokeWidth="1.5"
      />
      <rect x="500" y="22" width="200" height="3" fill="url(#pagoda-gold)" />

      {/* Central finial / jewel */}
      <g transform="translate(600, 8)">
        <circle cx="0" cy="-6" r="6" fill="url(#pagoda-gold)" />
        <path d="M 0 -12 L 3 -22 L -3 -22 Z" fill="url(#pagoda-gold)" />
        <circle cx="0" cy="-6" r="2.5" fill="#A4202D" />
      </g>

      {/* Hanging lanterns — three between the lower and middle tiers */}
      {[360, 600, 840].map((cx) => (
        <g key={cx} transform={`translate(${cx}, 130)`}>
          <line x1="0" y1="-20" x2="0" y2="-6" stroke="#C7A24A" strokeWidth="0.8" />
          <ellipse cx="0" cy="6" rx="9" ry="13" fill="#A4202D" stroke="#C7A24A" strokeWidth="1" />
          <line x1="-9" y1="6" x2="9" y2="6" stroke="#8B1A24" strokeWidth="0.6" />
          <path d="M -3 20 L 0 28 L 3 20 Z" fill="#C7A24A" />
        </g>
      ))}
    </svg>
  );
}
